import React, { useEffect, useState } from 'react';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProductEditorFields from '../../components/products/ProductEditorFields';
import ProductSheetPreview from '../../components/products/ProductSheetPreview';
import { productImageUrl } from '../../api/products';

export function getYoutubeEmbedUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.replace('/', '');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    return null;
  } catch {
    return null;
  }
}

export function ProductFormModal({ onClose, onSubmit, initialData, title }) {
  const [name, setName] = useState(initialData?.name ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [videoLink, setVideoLink] = useState(initialData?.videoLink ?? '');
  const [price, setPrice] = useState(
    typeof initialData?.price === 'number' ? String(initialData.price) : '',
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (imageFile) {
      const u = URL.createObjectURL(imageFile);
      setImagePreview(u);
      return () => URL.revokeObjectURL(u);
    }
    setImagePreview(productImageUrl(initialData) || '');
    return undefined;
  }, [imageFile, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      videoLink: videoLink.trim() || null,
      price: price ? Number(price) : 0,
      imageFile,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 md:p-4">
      <div
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-card shadow-elevation-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border p-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            <p className="text-xs text-muted-foreground">Fiche produit — aperçu en direct</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-muted">
            <Icon name="X" size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            <ProductEditorFields
              name={name}
              onNameChange={setName}
              description={description}
              onDescriptionChange={setDescription}
              price={price}
              onPriceChange={setPrice}
              videoLink={videoLink}
              onVideoLinkChange={setVideoLink}
              imagePreviewUrl={imagePreview}
              onImageFileChange={setImageFile}
              imageFileName={imageFile?.name || ''}
            />
            <ProductSheetPreview
              name={name}
              description={description}
              price={price}
              videoLink={videoLink}
              imageUrl={imagePreview}
            />
          </div>
          <div className="mt-4 flex justify-end gap-2 border-t border-border pt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" iconName="Check">
              Enregistrer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function VideoModal({ videoUrl, onClose }) {
  const youtubeEmbed = getYoutubeEmbedUrl(videoUrl);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-xl bg-card shadow-elevation-3">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">Vidéo produit</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-muted">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="flex items-center justify-center bg-black">
          {youtubeEmbed ? (
            <iframe
              src={youtubeEmbed}
              title="YouTube video"
              className="aspect-video w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="p-6 text-center">
              <p className="mb-4 text-sm text-muted-foreground">
                Lien non reconnu comme YouTube.
              </p>
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Icon name="ExternalLink" size={14} />
                Ouvrir le lien
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
